/**
 * GroupedTaskCard - Hierarchical Task Component
 * Displays a parent task with expandable subtasks, progress tracking, and smart expansion
 */

import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight, Lock, Users } from 'lucide-react';
import {
  calculateProgress,
  shouldExpandByDefault,
  hasCurrentSubtask,
  getNextSubtaskPreview,
  isOverdue,
  getDaysUntilDue,
  formatDueDate,
  getTimeEstimate,
  formatTimeEstimate
} from '../../utils/taskUtils';
import SubtaskItem from './SubtaskItem';

const GroupedTaskCard = ({
  parentTask,
  subtasks = [],
  index = 0,
  onTaskClick,
  onSubtaskComplete,
  onAISupport,
  className = ''
}) => {
  // Calculate if should be expanded by default
  const [isExpanded, setIsExpanded] = useState(
    shouldExpandByDefault(parentTask, subtasks, index)
  );

  // Recalculate expansion when subtasks change
  useEffect(() => {
    if (index === 0) {
      setIsExpanded(shouldExpandByDefault(parentTask, subtasks, index));
    }
  }, [subtasks, index, parentTask]);

  const progress = calculateProgress(subtasks);
  const hasCurrent = hasCurrentSubtask(subtasks);
  const nextPreview = getNextSubtaskPreview(subtasks);
  const overdue = isOverdue(parentTask);
  const daysUntil = getDaysUntilDue(parentTask);
  const dueDateText = formatDueDate(parentTask.due_date);
  const timeEstimate = getTimeEstimate(parentTask);

  // Determine card styling based on state
  const isComplete = parentTask.status === 'completed';
  const isFoundation = parentTask.is_foundation_task;
  const unlockCount = parentTask.unlock_count || 0;

  // Border and background styling
  const cardBorder = isFoundation
    ? 'border-l-4 border-l-blue-500'
    : hasCurrent && !isExpanded
    ? 'border-l-4 border-l-green-500'
    : 'border-l-4 border-l-transparent';

  const cardBg = isComplete
    ? 'bg-gray-50'
    : 'bg-white';

  const titleStyle = isComplete
    ? 'text-gray-500 line-through'
    : 'text-gray-900';

  return (
    <div
      className={`${cardBg} rounded-lg border border-gray-200 shadow-sm ${cardBorder} ${className}`}
    >
      {/* Header - Clickable to expand/collapse */}
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-start gap-3">
          {/* Priority Number */}
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 font-semibold text-sm flex items-center justify-center mt-1">
            {index + 1}
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Title Row */}
            <div className="flex items-start justify-between gap-2 mb-2">
              <h3 className={`text-base font-semibold ${titleStyle} flex-1`}>
                {parentTask.title}
              </h3>

              {/* Expand/Collapse Chevron */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsExpanded(!isExpanded);
                }}
                className="flex-shrink-0 p-1 hover:bg-gray-200 rounded transition-colors"
              >
                {isExpanded ? (
                  <ChevronDown className="w-5 h-5 text-gray-600" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-600" />
                )}
              </button>
            </div>

            {/* Progress Bar - Only show if task has subtasks */}
            {subtasks.length > 0 && (
              <div className="mb-2">
                <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                  <span>
                    {progress.completed} of {progress.total} steps complete
                  </span>
                  <span className="font-medium">{progress.percentage}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                  <div
                    className={`h-full transition-all duration-500 ${
                      progress.percentage === 100
                        ? 'bg-green-500'
                        : 'bg-indigo-500'
                    }`}
                    style={{ width: `${progress.percentage}%` }}
                  />
                </div>
              </div>
            )}

            {/* Badges and Metadata Row */}
            <div className="flex items-center gap-2 flex-wrap">
              {/* Foundation Task Badge */}
              {isFoundation && unlockCount > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                  <Users className="w-3 h-3" />
                  Unlocks {unlockCount} task{unlockCount !== 1 ? 's' : ''}
                </span>
              )}

              {/* Overdue Badge */}
              {overdue && (
                <span className="inline-flex items-center px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded-full">
                  {Math.abs(daysUntil)} day{Math.abs(daysUntil) !== 1 ? 's' : ''} overdue
                </span>
              )}

              {/* Due Date */}
              {!isComplete && parentTask.due_date && (
                <span className={`text-xs ${
                  overdue
                    ? 'text-red-600 font-medium'
                    : daysUntil !== null && daysUntil <= 3
                    ? 'text-orange-600 font-medium'
                    : 'text-gray-600'
                }`}>
                  Due {dueDateText}
                </span>
              )}

              {/* Time Estimate */}
              {timeEstimate && (
                <span className="text-xs text-gray-500">
                  {formatTimeEstimate(timeEstimate)}
                </span>
              )}

              {/* Next Preview Badge (when collapsed with current subtask) */}
              {!isExpanded && hasCurrent && nextPreview && (
                <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full animate-pulse">
                  {nextPreview}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Subtasks List - Expanded State */}
      {isExpanded && subtasks.length > 0 && (
        <div className="border-t border-gray-200 bg-gray-50 px-4 py-3">
          <div className="space-y-2">
            {subtasks
              .sort((a, b) => {
                const seqA = a.metadata?.workflow_sequence || 0;
                const seqB = b.metadata?.workflow_sequence || 0;
                return seqA - seqB;
              })
              .map((subtask) => (
                <SubtaskItem
                  key={subtask.id}
                  subtask={subtask}
                  onComplete={onSubtaskComplete}
                  onAISupport={onAISupport}
                  onTaskClick={onTaskClick}
                />
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupedTaskCard;
